/**
 * Side panel sections of the drawing power tools (phase 4): the structure list (select, rename, hide, delete, add a
 * garage or a shed; each structure's eave height by storeys, metres or two picked points), the selected dormer, and
 * the selected footprint side (gable ↔ hip, its pitch). Presentational: logic in structures.ts, dormers.ts and
 * footprint.ts; styles `.sk-structure…`, `.sk-dormer…`, `.sk-side-card…` in sketch.css.
 */
import { Building2, Car, Eye, EyeOff, Home, Pencil, Plus, Ruler, Trash2, Warehouse } from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import type { MessageKey } from "../i18n/keys";
import { DORMER_KINDS, type DormerKind, type SkDormer } from "./dormers.ts";
import { PITCH_CHIPS, STOREY_CHIPS } from "./roofModel.ts";
import type { SkStructure, StructureKind } from "./skeleton.ts";
import { pitchRise } from "./SketchCanvas";
import { structureLabel } from "./structures.ts";
import type { Text } from "./text.ts";
import { kbd, Msg, useSketchText } from "./useText.ts";

/** "8/12 · 33.7°" */
const pitchLine = (tx: Text, deg: number) => tx.t("sketch.pitch.riseAndDegrees", { rise: tx.t("sketch.pitch.rise", { rise: tx.upTo(pitchRise(deg), 1) }), deg: tx.num(deg, 1) });

const KIND_ICON: Record<StructureKind, ReactNode> = {
  house: <Home size={15} />,
  garage: <Warehouse size={15} />,
  shed: <Building2 size={15} />,
  carport: <Car size={15} />,
  other: <Building2 size={15} />,
};
const ADD_KINDS: [StructureKind, MessageKey][] = [
  ["garage", "sketch.structure.garage"],
  ["shed", "sketch.structure.shed"],
  ["carport", "sketch.structure.carport"],
  ["other", "sketch.obstacle.other"],
];

export interface StructureRow {
  structure: SkStructure;
  faces: number;
  area: number;
  kw: number;
}

export function StructuresSection(props: {
  rows: StructureRow[];
  active: string;
  readOnly: boolean;
  picking: boolean;
  onActive: (id: string) => void;
  onRename: (id: string, name: string) => void;
  onHidden: (id: string, hidden: boolean) => void;
  onDelete: (id: string) => void;
  onAdd: (kind: StructureKind) => void;
  onEave: (id: string, metres: number) => void;
  onPick: () => void;
}) {
  const tx = useSketchText();
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const active = props.rows.find((r) => r.structure.id === props.active) ?? props.rows[0];
  const eave = active?.structure.eaveHeight ?? 3;
  return (
    <section className="sk-side-section sk-structures" aria-label={tx.t("sketch.structures.title")}>
      <div className="sk-side-head">
        <span className="sk-side-icon"><Building2 size={16} /></span>
        <h3>{tx.t("sketch.structures.title")}</h3>
        <span className="sk-eyebrow">{tx.t("sketch.shading.buildings", { count: props.rows.length })}</span>
      </div>
      <ul className="sk-structure-list" role="listbox" aria-label={tx.t("sketch.structures.list")}>
        {props.rows.map((row, i) => {
          const s = row.structure;
          const name = structureLabel(tx, s.name);
          const isActive = s.id === active?.structure.id;
          return (
            <li key={s.id} className={`sk-structure${isActive ? " is-active" : ""}${s.hidden ? " is-hidden" : ""}`} role="option" aria-selected={isActive} style={{ "--i": i } as CSSProperties}>
              <button type="button" className="sk-structure-main" onClick={() => props.onActive(s.id)} title={tx.t("sketch.structures.selectTitle")}>
                <span className={`sk-structure-glyph is-${s.kind}`}>{KIND_ICON[s.kind]}</span>
                <span className="sk-structure-text">
                  {editing === s.id ? (
                    <NameInput value={name} onDone={(typed) => { setEditing(null); if (typed && typed !== name) props.onRename(s.id, typed); }} />
                  ) : (
                    <b dir="auto">{name}</b>
                  )}
                  <small>
                    {tx.t("sketch.structures.faces", { count: row.faces })}
                    {row.kw > 0 ? <> · <bdi className="sk-structure-kw">{tx.num(row.kw, 2)} kW</bdi></> : row.faces ? <> · <bdi>{tx.num(row.area, 0)} m²</bdi></> : null}
                  </small>
                </span>
              </button>
              <span className="sk-structure-actions">
                <button type="button" className="sk-icon-action" aria-label={tx.t("sketch.structures.rename", { name })} title={tx.t("sketch.structures.renameShort")} disabled={props.readOnly} onClick={() => setEditing(s.id)}><Pencil size={14} /></button>
                <button type="button" className="sk-icon-action" aria-label={tx.t(s.hidden ? "sketch.structures.show" : "sketch.structures.hide", { name })} title={tx.t(s.hidden ? "sketch.structures.showShort" : "sketch.structures.hideTitle")} aria-pressed={!!s.hidden} onClick={() => props.onHidden(s.id, !s.hidden)}>
                  {s.hidden ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
                <button type="button" className="sk-icon-action is-danger" aria-label={tx.t("sketch.structures.delete", { name })} title={tx.t(s.id === "main" ? "sketch.structures.deleteRoof" : "sketch.common.delete")} disabled={props.readOnly || (s.id === "main" && !row.faces)} onClick={() => props.onDelete(s.id)}><Trash2 size={14} /></button>
              </span>
            </li>
          );
        })}
      </ul>
      {active && (
        <div className="sk-field sk-structure-eave">
          <span className="sk-label">
            {tx.t("sketch.structures.eave", { name: structureLabel(tx, active.structure.name) })} <span>{tx.t("sketch.unit.m", { value: tx.num(eave, 2) })}</span>
          </span>
          <div className="sk-chips">
            {STOREY_CHIPS.map(([storeys, m]) => (
              <button key={storeys} type="button" className={`sk-chip${Math.abs(eave - m) < 0.01 ? " is-active" : ""}`} disabled={props.readOnly} onClick={() => props.onEave(active.structure.id, m)}>
                {tx.t("sketch.storeys", { count: storeys })}
              </button>
            ))}
            <span className="sk-number-wrap">
              <input className="sk-number" type="number" min={0} max={150} step={0.1} value={Math.round(eave * 100) / 100} disabled={props.readOnly} aria-label={tx.t("sketch.structures.eaveLabel", { name: structureLabel(tx, active.structure.name) })} onChange={(e) => props.onEave(active.structure.id, Math.max(0, Number(e.target.value) || 0))} />
              <span className="sk-unit">m</span>
            </span>
          </div>
          <button type="button" className={`sk-action is-ghost sk-pick${props.picking ? " is-active" : ""}`} disabled={props.readOnly} onClick={props.onPick} aria-pressed={props.picking}>
            <Ruler size={14} /> {tx.t(props.picking ? "sketch.structures.picking" : "sketch.structures.pick")}
          </button>
        </div>
      )}
      <div className="sk-structure-add">
        {adding ? (
          <div className="sk-chips" role="group" aria-label={tx.t("sketch.structures.addLabel")}>
            {ADD_KINDS.map(([kind, label]) => (
              <button key={kind} type="button" className="sk-chip" onClick={() => { setAdding(false); props.onAdd(kind); }}>
                {KIND_ICON[kind]} {tx.t(label)}
              </button>
            ))}
          </div>
        ) : (
          <button type="button" className="sk-action is-ghost" disabled={props.readOnly} onClick={() => setAdding(true)}>
            <Plus size={14} /> {tx.t("sketch.structures.add")}
          </button>
        )}
      </div>
    </section>
  );
}

function NameInput({ value, onDone }: { value: string; onDone: (v: string) => void }) {
  const tx = useSketchText();
  const [text, setText] = useState(value);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  return (
    <input
      ref={ref}
      className="sk-structure-name"
      value={text}
      maxLength={60}
      aria-label={tx.t("sketch.structures.nameLabel")}
      dir="auto"
      onChange={(e) => setText(e.target.value)}
      onBlur={() => onDone(text.trim())}
      onKeyDown={(e) => {
        if (e.key === "Enter") onDone(text.trim());
        if (e.key === "Escape") onDone(value);
        e.stopPropagation();
      }}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

const DORMER_LABEL: Record<DormerKind, MessageKey> = { gable: "sketch.dormer.gable", hip: "sketch.dormer.hip", shed: "sketch.dormer.shed" };
const DORMER_TITLE: Record<DormerKind, MessageKey> = { gable: "sketch.dormer.gableDormer", hip: "sketch.dormer.hipDormer", shed: "sketch.dormer.shedDormer" };

/** The selected dormer: its kind and sizes. */
export function DormerSection(props: { dormer: SkDormer; valid: boolean; readOnly: boolean; onChange: (d: SkDormer) => void; onDelete: () => void; onDeselect: () => void }) {
  const tx = useSketchText();
  const d = props.dormer;
  const field = (label: string, key: "width" | "depth" | "height", min: number, max: number, step: number) => (
    <label className="sk-dormer-field">
      <span>{label}</span>
      <span className="sk-number-wrap">
        <input className="sk-number" type="number" min={min} max={max} step={step} value={Math.round(d[key] * 100) / 100} disabled={props.readOnly} onChange={(e) => props.onChange({ ...d, [key]: Math.max(min, Math.min(max, Number(e.target.value) || min)) })} />
        <span className="sk-unit">m</span>
      </span>
    </label>
  );
  return (
    <section className="sk-side-section sk-dormer" aria-label={tx.t("sketch.toolsec.dormer.kinds")}>
      <div className="sk-side-head">
        <span className="sk-side-icon"><DormerArt kind={d.kind} /></span>
        <h3>{tx.t(DORMER_TITLE[d.kind])}</h3>
        <span className="sk-eyebrow">{tx.t("sketch.dormer.selected")}</span>
      </div>
      <div className="sk-segmented is-dormer" role="radiogroup" aria-label={tx.t("sketch.dormer.kindLabel")} style={{ "--i": DORMER_KINDS.indexOf(d.kind), "--n": 3 } as CSSProperties}>
        {DORMER_KINDS.map((k) => (
          <button key={k} type="button" role="radio" aria-checked={d.kind === k} className={d.kind === k ? "is-active" : ""} disabled={props.readOnly} onClick={() => props.onChange({ ...d, kind: k, pitch: k === "shed" ? Math.min(d.pitch, 15) : d.pitch < 20 ? 35 : d.pitch })}>
            {tx.t(DORMER_LABEL[k])}
          </button>
        ))}
      </div>
      <div className="sk-dormer-grid">
        {field(tx.t("sketch.dormer.width"), "width", 0.6, 12, 0.1)}
        {field(tx.t("sketch.dormer.depth"), "depth", 0.6, 12, 0.1)}
        {field(tx.t("sketch.dormer.frontWall"), "height", 0.2, 5, 0.1)}
      </div>
      <div className="sk-field">
        <span className="sk-label">{tx.t("sketch.dormer.itsRoof")} <span>{pitchLine(tx, d.pitch)}</span></span>
        <div className="sk-chips">
          {PITCH_CHIPS.filter(([, deg]) => deg > 0).map(([rise, deg]) => (
            <button key={rise} type="button" className={`sk-chip${Math.abs(d.pitch - deg) < 0.05 ? " is-active" : ""}`} disabled={props.readOnly} onClick={() => props.onChange({ ...d, pitch: deg })}>
              {tx.t("sketch.pitch.rise", { rise })}
            </button>
          ))}
        </div>
      </div>
      {!props.valid && <p className="sk-error">{tx.t("sketch.dormer.invalid")}</p>}
      <div className="sk-actions">
        <button type="button" className="sk-action is-danger" disabled={props.readOnly} onClick={props.onDelete}><Trash2 size={14} /> {tx.t("sketch.dormer.delete")}</button>
        <button type="button" className="sk-action is-ghost" onClick={props.onDeselect}>{tx.t("sketch.common.done")}</button>
      </div>
    </section>
  );
}

function DormerArt({ kind }: { kind: DormerKind }) {
  const roof = kind === "gable" ? "M4 11 L10 5 L16 11" : kind === "hip" ? "M4 11 L7.5 6 H12.5 L16 11" : "M4 9 L16 6";
  return (
    <svg width={18} height={18} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinejoin="round" aria-hidden>
      <path d="M1 18 L10 9 L19 18" opacity={0.45} />
      <path d={roof} />
      <path d={kind === "shed" ? "M5 9 V15 M15 6.3 V15" : "M5.5 10.5 V15 M14.5 10.5 V15"} />
    </svg>
  );
}

/** The selected line lies on a footprint side: flip it gable ↔ hip, set its pitch. */
export function FootprintSideCard(props: { structure: string; mode: "hip" | "gable"; pitch: number; allowed: boolean; readOnly: boolean; onToggle: () => void; onPitch: (deg: number) => void }) {
  const tx = useSketchText();
  return (
    <section className="sk-side-section sk-side-card" aria-label={tx.t("sketch.side.label")}>
      <div className="sk-side-head">
        <span className="sk-side-icon"><GableGlyph gable={props.mode === "gable"} /></span>
        <h3>{tx.t("sketch.side.title", { name: structureLabel(tx, props.structure) })}</h3>
        <span className="sk-eyebrow"><Msg id="sketch.side.flips" values={{ kbd }} /></span>
      </div>
      <div className="sk-segmented is-side" role="radiogroup" aria-label={tx.t("sketch.side.side")} style={{ "--i": props.mode === "hip" ? 0 : 1, "--n": 2 } as CSSProperties}>
        <button type="button" role="radio" aria-checked={props.mode === "hip"} className={props.mode === "hip" ? "is-active" : ""} disabled={props.readOnly} onClick={() => props.mode !== "hip" && props.onToggle()}>
          <GableGlyph gable={false} /> {tx.t("sketch.dormer.hip")}
        </button>
        <button type="button" role="radio" aria-checked={props.mode === "gable"} className={props.mode === "gable" ? "is-active" : ""} disabled={props.readOnly || !props.allowed} onClick={() => props.mode !== "gable" && props.onToggle()} title={props.allowed ? undefined : tx.t("sketch.side.gableTitle")}>
          <GableGlyph gable /> {tx.t("sketch.side.gableEnd")}
        </button>
      </div>
      {!props.allowed && <p className="sk-caption">{tx.t("sketch.side.insideCorner")}</p>}
      {props.mode === "hip" && (
        <div className="sk-field">
          <span className="sk-label">{tx.t("sketch.side.pitch")} <span>{pitchLine(tx, props.pitch)}</span></span>
          <div className="sk-chips">
            {PITCH_CHIPS.filter(([, deg]) => deg > 0).map(([rise, deg]) => (
              <button key={rise} type="button" className={`sk-chip${Math.abs(props.pitch - deg) < 0.05 ? " is-active" : ""}`} disabled={props.readOnly} onClick={() => props.onPitch(deg)}>
                {tx.t("sketch.pitch.rise", { rise })}
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function GableGlyph({ gable }: { gable: boolean }) {
  return (
    <svg width={18} height={16} viewBox="0 0 20 16" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinejoin="round" aria-hidden>
      {gable ? <path d="M2 14 V8 L10 2 L18 8 V14 Z" /> : <path d="M2 14 L6 6 H14 L18 14 Z" />}
    </svg>
  );
}
